import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PhoneIcon,
  BookOpenIcon,
  UserGroupIcon,
  WalletIcon,
  ArrowPathIcon,
  BuildingLibraryIcon,
  SparklesIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import platformSettingsService, {
  Contacts,
  Instructions,
  ReferralSettings,
  AgentReferralSettings,
  WalletSettings,
  SpinSettings,
} from '../api/services/platformSettings.service';
import ContactsTab from '../components/platformSettings/ContactsTab';
import InstructionsTab from '../components/platformSettings/InstructionsTab';
import ReferralTab from '../components/platformSettings/ReferralTab';
import WalletTab from '../components/platformSettings/WalletTab';
import BanksTab from '../components/platformSettings/BanksTab';
import SpinTab from '../components/platformSettings/SpinTab';
import HousePlayersTab from '../components/platformSettings/HousePlayersTab';

type TabId =
  | 'contacts'
  | 'instructions'
  | 'referral'
  | 'wallet'
  | 'spin'
  | 'banks'
  | 'housePlayers';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  {
    id: 'contacts',
    label: 'Contacts',
    icon: <PhoneIcon className="w-4 h-4" />,
  },
  {
    id: 'instructions',
    label: 'Instructions',
    icon: <BookOpenIcon className="w-4 h-4" />,
  },
  {
    id: 'referral',
    label: 'Referral',
    icon: <UserGroupIcon className="w-4 h-4" />,
  },
  {
    id: 'wallet',
    label: 'Wallet',
    icon: <WalletIcon className="w-4 h-4" />,
  },
  {
    id: 'spin',
    label: 'Spin',
    icon: <SparklesIcon className="w-4 h-4" />,
  },
  {
    id: 'banks',
    label: 'Banks',
    icon: <BuildingLibraryIcon className="w-4 h-4" />,
  },
  {
    id: 'housePlayers',
    label: 'House Players',
    icon: <UsersIcon className="w-4 h-4" />,
  },
];

const PlatformSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabId>('contacts');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{
    type: 'success' | 'error';
    msg: string;
  } | null>(null);
  const [resetConfirm, setResetConfirm] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['platform-settings'],
    queryFn: () => platformSettingsService.get(),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  };

  const handleSave = async (
    patch:
      | { contacts: Contacts }
      | { instructions: Instructions }
      | {
          referral: ReferralSettings;
          agentReferral: AgentReferralSettings;
        }
      | { wallet: WalletSettings }
      | { spin: SpinSettings }
  ) => {
    setSaving(true);
    try {
      await platformSettingsService.update(patch);
      queryClient.refetchQueries({ queryKey: ['platform-settings'] });
      showToast('success', 'Platform settings saved');
    } catch (e: any) {
      showToast(
        'error',
        e?.response?.data?.message ||
          e?.message ||
          'Failed to save platform settings'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      await platformSettingsService.reset();
      queryClient.refetchQueries({ queryKey: ['platform-settings'] });
      showToast('success', 'Platform settings reset to defaults');
      setResetConfirm(false);
    } catch (e: any) {
      showToast(
        'error',
        e?.response?.data?.message ||
          e?.message ||
          'Failed to reset platform settings'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveContacts = async (contacts: Contacts) => {
    await handleSave({ contacts });
  };
  const handleSaveInstructions = async (instructions: Instructions) => {
    await handleSave({ instructions });
  };
  const handleSaveReferral = async (
    referral: ReferralSettings,
    agentReferral: AgentReferralSettings
  ) => {
    await handleSave({ referral, agentReferral });
  };
  const handleSaveWallet = async (wallet: WalletSettings) => {
    await handleSave({ wallet });
  };
  const handleSaveSpin = async (spin: SpinSettings) => {
    await handleSave({ spin });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-solid border-primary-500 border-t-transparent mx-auto" />
          <p className="mt-3 text-sm text-gray-500">
            Loading platform settings...
          </p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-red-500">
            {error instanceof Error
              ? error.message
              : 'Failed to load platform settings'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const agentReferralValue: AgentReferralSettings =
    data.agentReferral ?? {
      enabled: false,
      defaultRewardAmount: 50,
      minDepositAmount: 0,
      rewardWindowDays: 0,
      startedAt: null,
    };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Platform Settings
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Configure contact info, instructions, referral rewards, wallet
            preferences, spin prizes, payment accounts, and house players.
          </p>
        </div>
        {tab !== 'banks' && tab !== 'housePlayers' && (
          <button
            onClick={() => setResetConfirm(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white text-red-600 border border-red-200 hover:bg-red-50"
          >
            <ArrowPathIcon className="w-3.5 h-3.5" />
            Reset to Defaults
          </button>
        )}
      </div>

      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  active
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {t.icon}
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'contacts' && (
        <ContactsTab
          value={data.contacts}
          onSave={handleSaveContacts}
          saving={saving}
        />
      )}
      {tab === 'instructions' && (
        <InstructionsTab
          value={data.instructions}
          onSave={handleSaveInstructions}
          saving={saving}
        />
      )}
      {tab === 'referral' && (
        <ReferralTab
          value={data.referral}
          agentValue={agentReferralValue}
          onSave={handleSaveReferral}
          saving={saving}
        />
      )}
      {tab === 'wallet' && (
        <WalletTab
          value={data.wallet}
          onSave={handleSaveWallet}
          saving={saving}
        />
      )}
      {tab === 'spin' && (
        <SpinTab
          spin={data.spin ?? { enabled: true, coinAmount: 1 }}
          onSaveSpin={handleSaveSpin}
          saving={saving}
        />
      )}
      {tab === 'banks' && <BanksTab />}
      {tab === 'housePlayers' && <HousePlayersTab />}

      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {resetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setResetConfirm(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              Reset all platform settings?
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              This will restore all platform settings to their default
              values. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setResetConfirm(false)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                disabled={saving}
              >
                {saving ? 'Resetting...' : 'Reset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformSettings;